import type { FeatureCollection } from 'geojson';

import type { GeojsonShape } from '@/common/types/util.types';

export interface Flight {
  display_date: string;
  flight_dates: string[];
}

export interface Zone {
  client_id: number;
  client_name: string;
  description: string;
  geojson: GeojsonShape;
  name: string;
  workspace: string;
  zone: number;
  areaSize?: number;
  flights?: Flight[];
}

/**
 * BE-reserved id bands (interface-tiler, `ephemeral-exploration-zone` contract):
 *   real zones        `< 9_000_000`
 *   staging `zone:03`  `[9_000_000, 10_000_000)` — stored drawings, inline geometry
 *   exploration `zone:04` `>= 10_000_000` — ephemeral self-serve projects (shells of zone:03 children)
 */
export const STAGING_ZONE_MIN_ID = 9_000_000;
export const EXPLORATION_ZONE_MIN_ID = 10_000_000;

/**
 * Sentinel `zone` id for the synthetic, in-memory zone that backs draw-only
 * Explore mode *before* it is saved (no persisted project/zone). Negative so it
 * can never collide with a real, staging, or exploration id. Distinct from a
 * saved exploration (`zone:04`, id >= EXPLORATION_ZONE_MIN_ID).
 */
export const EXPLORE_ZONE_ID = -1;

/** Staging zones (`zone:03`) carry inline geometry and skip the degenerate/AOI-resolution path. */
export const isStagingZone = (zone: Pick<Zone, 'zone'>): boolean =>
  zone.zone >= STAGING_ZONE_MIN_ID && zone.zone < EXPLORATION_ZONE_MIN_ID;

/** Saved ephemeral exploration projects (`zone:04`). */
export const isExplorationZone = (zone: Pick<Zone, 'zone'>): boolean => zone.zone >= EXPLORATION_ZONE_MIN_ID;

/**
 * Whether a layer is a zone's **AOI boundary**, matched by intent rather than by
 * its (user-facing, translatable) display name. The BE's `layer_id` is the full
 * sort key — `layer:<source>:<canonical_key>:<date>` (e.g.
 * `layer:vector_tile:aoi:20161101`) — whose `<canonical_key>` segment is the
 * stable identifier (`dynamo._layer_key`), independent of the display `name`
 * (which an operator may set to anything, e.g. "Farm"). We key off that segment.
 *
 * Kept as a union with `name === 'aoi'` so the client-synthesized boundary
 * (which has no `layer_id`) and legacy `aoi`-named records stay recognised — a
 * `layer_id`-only check would make the boundary fallback synthesize duplicates
 * for them. Structurally typed so it accepts both the processed `ZoneLayer` and
 * the raw `/layers` record.
 */
export const isAoiLayer = (layer: {
  name?: string | null;
  layer_id?: string | null;
}): boolean => layer.name === 'aoi' || (layer.layer_id ?? '').split(':').includes('aoi');

export interface Multizone {
  description: string | null;
  geojson: FeatureCollection;
  name: string;
  zone: number;
  zones: number[];
  /**
   * Child zone names, parallel to `zones` (ids). Supplied by the listing
   * call so the home-page search can match a project by the names of the
   * zones it contains — not just their ids. Optional: absent until the
   * interface-tiler `zone-listing-child-names` change ships, so search
   * degrades gracefully (no zone-name matches) before then.
   */
  zone_names?: string[];
  areaSize?: number;
}

export type MultizoneWithoutGeoJSON = Omit<Multizone, 'geojson'>;
export type MultizoneWithZonesData = Multizone & { zonesData: Zone[] };
